import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, effect, signal, computed, Signal, inject, untracked } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { signInWithCustomToken, signOut } from '@angular/fire/auth';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SessionService } from '../session.service';

interface CustomTokenResponse {
  token: string;
}

type AuthState = 'signed-out' | 'signing-in' | 'signed-in';

/**
 * Firebase custom-token auth service.
 *
 * Usage
 * - Call `initialize(baseDomain)` once at app start. The service then
 *   reacts to `SessionService` changes to sign in/out of Firebase Auth.
 *
 * Backend endpoint (must be provided by the domain passed to `initialize()`)
 * - GET `${baseDomain}/firebase/custom-token`
 * - Sends credentials (cookies) with the request.
 * - Responds with `{ token: string }` (Firebase Custom Token for the SAME
 *   Firebase project as the frontend app).
 *
 * Notes
 * - If you see `auth/custom-token-mismatch`, the token was minted for a
 *   different Firebase project than the app is initialized with.
 */
@Injectable()
export class FirebaseCustomAuthService {
  private readonly http = inject(HttpClient);
  private readonly sessionService = inject(SessionService);
  private readonly auth = inject(Auth);

  private readonly authStateSignal = signal<AuthState>('signed-out');
  private customTokenUrl!: string;

  /**
   * Current authentication state
   */
  public readonly authState: Signal<AuthState> = this.authStateSignal.asReadonly();

  /**
   * Whether the user is signed in to Firebase
   */
  public readonly isSignedIn: Signal<boolean> = computed(() => this.authStateSignal() === 'signed-in');

  /**
   * Whether sign-in is currently in progress
   */
  public readonly isSigningIn: Signal<boolean> = computed(() => this.authStateSignal() === 'signing-in');

  constructor() {
    effect((onCleanup) => {
      const sessionId = this.sessionService.getSessionIdSignal()();

      untracked(() => {
        if (!this.customTokenUrl) {
          return;
        }

        if (sessionId) {
          const signIn = this.signInWithSession();
          onCleanup(() => signIn.unsubscribe());
        } else {
          this.signOutFirebase();
        }
      });
    });
  }

  public initialize(baseDomain: string): void {
    this.customTokenUrl = `${baseDomain}/firebase/custom-token`;
  }

  /**
   * Fetch custom token from backend and sign in to Firebase Auth
   */
  private signInWithSession(): Subscription {
    this.authStateSignal.set('signing-in');

    return this.http
      .get<CustomTokenResponse>(this.customTokenUrl, { withCredentials: true })
      .pipe(switchMap((response) => signInWithCustomToken(this.auth, response.token)))
      .subscribe({
        next: () => this.authStateSignal.set('signed-in'),
        error: (error) => {
          const failedStep = error instanceof HttpErrorResponse ? 'fetch custom token from backend' : 'sign in';
          console.error(`[FirebaseAuth] Failed to ${failedStep}:`, error);
          this.authStateSignal.set('signed-out');
        },
      });
  }

  /**
   * Sign out from Firebase Auth
   */
  private async signOutFirebase(): Promise<void> {
    try {
      await signOut(this.auth);
      this.authStateSignal.set('signed-out');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  }
}
